import styles from "./affordability.module.css"

import React from "react"
import cx from "classnames"
import {Wizard} from "activities/components/wizard"
import * as str from "shared/string"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as pie from "activities/components/charts/pie"

const MonthlyIncomeQuestion = ({resetKey, updateState, monthlyIncome, step}) => {
  const question = step.details.question
  return (
    <div className={styles["rent-question"]}>
      <div id={`${str.toKebab(question)}`} className={cx(styles["lg-question"], fonts.getFontClass())}>
        {question}
      </div>
      <inputs.LongCalcInput
        ariaLabelledby={`${str.toKebab(question)}`}
        resetKey={resetKey}
        onChange={updateState("monthlyIncome", util.fromString)}
        integerLimit={5}
        initialValue={monthlyIncome}
      />
    </div>
  )
}

const MonthlyExpensesQuestion = (props) => {
  const {resetKey, updateState, monthlyIncome, monthlyExpenses, monthlyDebts, theme, setState, colorClaims, step} = props
  const {monthlyExpenseOptions, monthlyDebtOptions, colors, expenseIntegerLimit, debtIntegerLimit} = step.details
  const expenseColors = util.mapColors(colorClaims, colors, "monthlyExpenses")
  const debtColors = util.mapColors(colorClaims, colors, "monthlyDebts")
  return (
    <div className={styles["large-selections"]}>
      <div className="selections large">
        <inputs.ExpenseForm
          className={styles["form"]}
          entryClassName={styles["dropdown"]}
          containerClassName={styles["expense-container"]}
          title="Monthly Expenses"
          initialValue={monthlyExpenses}
          integerLimit={expenseIntegerLimit}
          onRowAdd={util.addRow(setState, "monthlyExpenses")}
          onRowRemove={util.removeRow(setState, "monthlyExpenses")}
          onRowChange={util.updateRow(setState, "monthlyExpenses")}
          resetKey={resetKey}
          options={monthlyExpenseOptions}
          colors={expenseColors}
          theme={theme}
          min={3}
          max={monthlyExpenseOptions.length}
        />
        <inputs.ExpenseForm
          className={styles["form"]}
          entryClassName={styles["dropdown"]}
          containerClassName={styles["expense-container"]}
          title="Monthly Debts"
          initialValue={monthlyDebts}
          integerLimit={debtIntegerLimit}
          onRowAdd={util.addRow(setState, "monthlyDebts")}
          onRowRemove={util.removeRow(setState, "monthlyDebts")}
          onRowChange={util.updateRow(setState, "monthlyDebts")}
          resetKey={resetKey}
          options={monthlyDebtOptions}
          colors={debtColors}
          theme={theme}
          min={2}
          max={monthlyDebtOptions.length}
        />
      </div>
    </div>
  )
}

const getTotal = (expenses) => {
  const amounts = expenses.map((expense) => expense.cost).filter((x) => x !== undefined)
  return amounts.reduce((acc, val) => acc + val)
}

const getResults = (leftForRentPercent, leftForRent, results) => {
  switch (true) {
    case leftForRentPercent < 0 && leftForRent < 0:
      return results["over-budget"]
    case leftForRent < 200 && leftForRent >= 0:
      return results["get-a-roommate"]
    case leftForRentPercent < 0.23 && leftForRentPercent > 0:
      return results["under-recommended"]
    case leftForRentPercent > 0.25:
      return results["over-recommended"]
    case leftForRentPercent > 0.23 && leftForRentPercent <= 0.25:
      return results["just-right"]
    default:
      return results["under-recommended"]
  }
}

const pieColors = ["#FF91B0", "#C4A8DF", "#FFBB00"]

const Results = ({projectedMonthlyPayment, monthlyIncome, monthlyExpenses, monthlyDebts, theme, step}) => {
  const leftForRent = monthlyIncome - getTotal(monthlyDebts) - getTotal(monthlyExpenses)
  const leftForRentPercent = leftForRent / monthlyIncome
  const monthlyExpensesPercent = getTotal(monthlyExpenses) / monthlyIncome
  const monthlyDebtsPercent = getTotal(monthlyDebts) / monthlyIncome
  const recommendedRent = monthlyIncome * 0.25
  const result = getResults(leftForRentPercent, leftForRent, step.details.results)
  const pieData = [
    {name: "Available Income", value: leftForRent},
    {name: "Monthly Expenses", value: getTotal(monthlyExpenses)},
    {name: "Monthly Debts", value: getTotal(monthlyDebts)}
  ]
  const innerPieData = [{name: "Recommended Rent", value: recommendedRent}]
  const innerPieDataAngle = 360 * (recommendedRent / monthlyIncome)
  return (
    <div className={styles["rent-results"]}>
      <div className={styles["results-text"]}>
        <div className={fonts.getFontClass()}>
          <h4>Income Available for Rent</h4>
          <div className={styles["left-for-rent"]}>{util.formatDollarsNoCents(leftForRent)}</div>
          <div className={styles["rent-message"]}>
            {result(util.formatDollarsNoCents(leftForRent), util.formatDollarsNoCents(recommendedRent), util.formatPercent(leftForRentPercent))}
          </div>
        </div>
        <div className={cx(styles["legend"], fonts.getFontClass())}>
          <ul>
            <li>
              <div>
                <span className={styles["dot"]} style={{background: "#" + theme["color-1"]}}></span>Recommended Rent
              </div>
              <div>
                <span className={styles["xsm-amounts"]}>{util.formatDollarsNoCents(recommendedRent)}</span> <span className={styles["percents"]}>25%</span>
              </div>
            </li>
            <li>
              <div>
                <span className={styles["dot"]} style={{background: "#FF91B0"}}></span>Available Income
              </div>
              <div>
                <span className={styles["xsm-amounts"]}>{util.formatDollarsNoCents(leftForRent)}</span>{" "}
                <span className={styles["percents"]}>{util.formatPercent(leftForRentPercent)}</span>
              </div>
            </li>
            <li>
              <div>
                <span className={styles["dot"]} style={{background: "#C4A8DF"}}></span>Monthly Expenses
              </div>
              <div>
                <span className={styles["xsm-amounts"]}>{util.formatDollarsNoCents(getTotal(monthlyExpenses))}</span>{" "}
                <span className={styles["percents"]}>{util.formatPercent(monthlyExpensesPercent)}</span>
              </div>
            </li>
            <li>
              <div>
                <span className={styles["dot"]} style={{background: "#FFBB00"}}></span>Monthly Debts
              </div>
              <div>
                <span className={styles["xsm-amounts"]}>{util.formatDollarsNoCents(getTotal(monthlyDebts))}</span>{" "}
                <span className={styles["percents"]}>{util.formatPercent(monthlyDebtsPercent)}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className={styles["results-pie"]} role="img" aria-label="Chart that demonstrates how much money is available for rent monthly.">
        <shared.Chart className={styles["chart"]} aria-hidden="true">
          <pie.PieChart
            colors={pieColors}
            data={pieData}
            dataKey="value"
            label="Monthly Income"
            innerData={innerPieData}
            innerDataAngle={innerPieDataAngle}
            innerDataColor={"#" + theme["color-1"]}
          />
        </shared.Chart>
      </div>
    </div>
  )
}

export default (props) => <RentAffordabilityCalculator_ {...props} />

const RentAffordabilityCalculator_ = shared.ActivityFactory(
  ({getProps, getState, updateState, setState, onReset}) => {
    const {resetKey, monthlyIncome, monthlyExpenses, monthlyDebts, colorClaims} = getState()
    const {steps, theme} = getProps()
    return (
      <div className={styles["wizard"]}>
        <Wizard
          resetKey={resetKey}
          onReset={onReset}
          setup={{
            steps: steps,
            theme: theme,
            props: {
              monthlyIncome: monthlyIncome,
              monthlyExpenses: monthlyExpenses,
              monthlyDebts: monthlyDebts,
              colorClaims: colorClaims,
              setState: setState,
              theme: theme,
              updateState: updateState
            }
          }}
        />
      </div>
    )
  },
  {
    defaultProps: {
      className: styles["rent-affordability-calculator"],
      steps: [
        {
          title: "Rent Affordability Calculator",
          subtitle: "With a little information, you can find out how much money you have available for rent each month and get an idea for what you can comfortably afford.",
          isStepComplete: (stepProps) => (stepProps.monthlyIncome > 0 ? true : false),
          onChangeStep: (setState, {monthlyIncome}) => setState({monthlyIncome}),
          content: MonthlyIncomeQuestion,
          details: {
            question: "What is your gross monthly income?"
          },
          buttonText: "Next"
        },
        {
          title: "Rent Affordability Calculator",
          subtitle: "Next, estimate how much you spend each month in various categories.",
          isStepComplete: () => true,
          onChangeStep: (setState, {monthlyExpenses, monthlyDebts}) => setState({monthlyExpenses, monthlyDebts}),
          content: MonthlyExpensesQuestion,
          details: {
            colors: [
              "#FFBB00",
              "#64D254",
              "#C4A8DF",
              "#FF91B0",
              "#FF4444",
              "#6EC5EF",
              "#005F8B",
              "#00BDDC",
              "#65DFC5",
              "#9E6CC7",
              "#FF881F",
              "#FFE7AF",
              "#00637A",
              "#FFCBCF",
              "#006AF9",
              "#FFA971",
              "#EDFCAE",
              "#A7DD00",
              "#077259",
              "#A8AFDA"
            ],
            monthlyExpenseOptions: ["Food", "Utilities", "Phone", "Internet", "Car Insurance", "Car Repairs", "Gas", "Entertainment", "Subscriptions", "Clothing", "Other"],
            monthlyDebtOptions: ["Credit Card", "Car Payment", "Medical Debt", "Student Loans", "Personal Loans", "Other"],
            expenseIntegerLimit: 5,
            debtIntegerLimit: 5
          },
          buttonText: "Next"
        },
        {
          title: "Rent Affordability Calculator",
          subtitle: "Check out how much you have left to spend on rent with this chart below.",
          isStepComplete: () => true,
          content: Results,
          details: {
            results: {
              "over-recommended": (leftForRent, recommendedRent) =>
                "While you have more available, it's recommended that you spend no more than 25% of your gross income on rent each month. For you, that would be " +
                recommendedRent +
                ".",
              "under-recommended": (leftForRent, recommendedRent, percentOfIncome) =>
                "That's " +
                percentOfIncome +
                " of your income—which lower than the recommended cap of 25% for rent, but it's never a bad idea to pay less if you can make it work. Otherwise, you can adjust your budget to make more available.",
              "just-right": (leftForRent) =>
                "You have " +
                leftForRent +
                " available for rent, which is 25% of your income. That's the recommended amount of your income to pay for rent, so you're right on target.",
              "over-budget": () =>
                "Your budgeted expenses are currently higher than your income. That means you'll probably need to adjust your budget or increase your income in order to afford paying rent.",
              "get-a-roommate": () =>
                "It may be difficult to find somewhere to rent on your own for what you have available, but that doesn't mean you're out of options. Splitting rent with roommates or living with a friend or family member can help your money go much further."
            }
          },
          buttonText: "Start Over"
        }
      ]
    },
    stateFilter: (state) => state,
    defaultState: {
      colorClaims: [],
      monthlyIncome: 0,
      monthlyExpenses: [
        {name: "Food", cost: 250},
        {name: "Entertainment", cost: 50},
        {name: "Gas", cost: 100}
      ],
      monthlyDebts: [{name: "Car Payment", cost: 230}]
    }
  }
)
